import { useEffect, useState } from 'react';
import { SUPABASE_URL } from '../supabase';
/* Presentational only — it reads no store, which is what keeps this page
   independent of the app it is bundled with. */
import Icon from './Icon';

/**
 * THE PAGE SOMEBODY OPENS WHO HAS NO ACCOUNT — #216.
 *
 * TRINRE's approvers are a client's sponsor, an executive director, a
 * department head. None of them will be given a login, so until now their
 * answer reached the sheet second-hand: somebody in the office typed it for
 * them. That is a note saying they agreed. This is the thing itself.
 *
 * WHY IT IS NOT PART OF THE APP, though it is in the same bundle. Nothing here
 * signs in, reads the store, or subscribes to anything — it is mounted in
 * main.tsx BEFORE <App/>, so a person on this page never starts a session, and
 * a broken session can never keep them off it. The only thing it can reach is
 * one function, and the only thing it holds is the token out of the address.
 *
 * THE SHAPE IS THE ONE VINNIE CHOSE ON 22 AUGUST — option B, the cover sheet,
 * with the gate on. A summary of what this is and who else is signing, the
 * document opening from it, the two answers underneath. It answers the question
 * an approver actually asks — has the CIO signed this yet? — which the document
 * on its own cannot.
 */
const SIGN = `${SUPABASE_URL}/functions/v1/sign`;

type Answer = 'approved' | 'rejected' | 'waiting';

type Row = { position: string; name: string; answer: Answer; at: string | null; why: string; isYou: boolean };

type Sheet = {
  state: 'ready' | 'answered' | 'signed' | 'rejected' | 'withdrawn' | 'superseded' | 'changed' | 'gone';
  you?: { name: string; position: string; answer: Answer; at: string | null; why: string; opened: boolean };
  document?: {
    /* Which subject this sheet is against. Absent on older replies is treated as
       a document, so nothing regresses. */
    subjectKind?: 'document' | 'sprint' | 'release';
    board: string;
    organisation: string;
    title: string;
    kind?: string;
    version?: number;
    sentAt: string | null;
    /* Document only. */
    fileName?: string;
    fileType?: string;
    fileSize?: number;
    /* Sprint only. */
    startsOn?: string | null;
    endsOn?: string | null;
    /* Sprint and release: what is in it, shown read-only. */
    items?: { ref: string; title: string; type: string; status: string }[];
  };
  panel?: Row[];
  fingerprint?: { askedAgainst: string; now: string };
  url?: string;
  error?: string;
};

/** The reader's own clock, because "22 August at 4:31pm" is only true where they are. */
const when = (at: string | null) =>
  at
    ? new Date(at).toLocaleString(undefined, {
        day: 'numeric',
        month: 'long',
        hour: 'numeric',
        minute: '2-digit',
      })
    : '';

const onThe = (at: string | null) =>
  at ? new Date(at).toLocaleDateString(undefined, { day: 'numeric', month: 'long' }) : '';

const size = (bytes: number) =>
  bytes >= 1_000_000 ? `${(bytes / 1_000_000).toFixed(1)} MB` : `${Math.max(1, Math.round(bytes / 1000))} KB`;

/** A hash is unreadable; four groups of four is comparable by eye. */
const inFours = (hash: string) => (hash.slice(0, 16).match(/.{1,4}/g) ?? []).join(' ');

/** A delivered item, marked by form as well as colour (a check, not just a
 *  green word) — the board's "Done" column, however it is cased. */
const isDone = (status: string) => (status ?? '').trim().toLowerCase() === 'done';

export default function Signing({ token }: { token: string }) {
  const [sheet, setSheet] = useState<Sheet | null>(null);
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState('');
  /* Null while nothing is being answered; the page is otherwise read-only. */
  const [saying, setSaying] = useState<'approved' | 'rejected' | null>(null);
  const [why, setWhy] = useState('');
  const [file, setFile] = useState('');

  const ask = async (body: Record<string, unknown>): Promise<Sheet> => {
    const r = await fetch(SIGN, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ token, ...body }),
    });
    return r.json();
  };

  useEffect(() => {
    let stop = false;
    ask({ action: 'look' })
      .then((s) => !stop && setSheet(s))
      /* A page that shows a spinner for ever is worse than one that says the
         link did not work — see the wording below, which reveals nothing. */
      .catch(() => !stop && setSheet({ state: 'gone' }));
    return () => {
      stop = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  if (!sheet) return <div className="sg-wait">Opening…</div>;

  /*
   * ONE WORDING FOR EVERY LINK THAT IS NOT A LIVE ONE.
   *
   * A wrong link, an expired one and a request that has been taken back all
   * land here, because telling them apart would make a wrong link a way to find
   * out that a document exists. It says what to do instead, which is the only
   * useful thing it can say.
   */
  if (sheet.state === 'gone')
    return (
      <Paper masthead="LINK NOT RECOGNISED">
        <p className="sg-stamp is-bad">
          <strong>This link has expired, or is not one we recognise.</strong>
        </p>
        <p className="sg-note">
          Ask whoever sent it to send another. Nothing about the document is shown here — a wrong
          link must not become a way to read one.
        </p>
      </Paper>
    );

  const doc = sheet.document!;
  const subjectKind = doc.subjectKind ?? 'document';
  const you = sheet.you!;
  const panel = sheet.panel ?? [];

  if (sheet.state === 'withdrawn' || sheet.state === 'superseded' || sheet.state === 'changed')
    return (
      <Paper
        masthead={sheet.state === 'changed' ? 'THE DOCUMENT CHANGED' : sheet.state === 'withdrawn' ? 'WITHDRAWN' : 'REPLACED'}
        organisation={doc.organisation}
      >
        <Heading doc={doc} you={you} />
        {sheet.state === 'changed' ? (
          <>
            <p className="sg-stamp is-bad">
              <strong>This is not the file you were sent.</strong> It was replaced after your link
              was issued, so it cannot be signed here.
            </p>
            <p className="sg-note">
              Whoever asked for your approval has the same information. You will be sent a new link
              if it still needs you.
            </p>
            <p className="sg-print">
              asked against {inFours(sheet.fingerprint?.askedAgainst ?? '')}
              <br />
              file now {inFours(sheet.fingerprint?.now ?? '')}
            </p>
          </>
        ) : sheet.state === 'withdrawn' ? (
          <>
            <p className="sg-stamp is-waiting">
              <strong>This request was taken back.</strong> Nothing is being asked of you.
            </p>
            <p className="sg-note">It is being revised. If it comes back you will be sent a new link.</p>
          </>
        ) : (
          <>
            <p className="sg-stamp is-waiting">
              <strong>A newer version of this has replaced it.</strong>
            </p>
            <p className="sg-note">
              Nothing further is needed from you on this one. If the new version needs your approval
              you will be sent a link for it.
            </p>
          </>
        )}
      </Paper>
    );

  const answered = you.answer !== 'waiting' && !!you.at;
  /*
   * WHEN AN ANSWER STOPS BEING CHANGEABLE, which is Vinnie's call of 22 August
   * with the one boundary it needs to stay a signature.
   *
   * Signed off: sealed for everybody. A release cut on four approvals cannot
   * have one of them withdrawn afterwards.
   *
   * Rejected: sealed for everybody EXCEPT whoever rejected it. The document is
   * already going back to be revised, so nobody else's approval means anything
   * — but a rejection typed by mistake has to be undoable by the person who
   * typed it, or the only way back is to withdraw the request and ask four
   * people all over again.
   */
  const mineIsTheRejection = you.answer === 'rejected' && !!you.at;
  const sealed =
    sheet.state === 'signed' || (sheet.state === 'rejected' && !mineIsTheRejection);

  const open = async () => {
    setBusy(true);
    setProblem('');
    const got = await ask({ action: 'open' });
    setBusy(false);
    /*
     * A DOCUMENT returns a file address to SHOW HERE rather than open in a new
     * tab — window.open after an await is a popup the browser may block, and a
     * blocked popup reads as "the button does nothing" on the one press the whole
     * feature depends on. A SPRINT or a RELEASE has no file — its content came
     * down with the sheet — so opening it only records the review and unlocks the
     * answer. An error is still an error either way.
     */
    if (got.error || (subjectKind === 'document' && !got.url)) {
      setProblem(got.error ?? 'It could not be opened.');
      return;
    }
    if (got.url) setFile(got.url);
    setSheet((s) => (s ? { ...s, you: { ...s.you!, opened: true } } : s));
  };

  const record = async (answer: 'approved' | 'rejected') => {
    setBusy(true);
    setProblem('');
    const got = await ask({ action: 'answer', answer, why });
    setBusy(false);
    if (got.error) {
      setProblem(got.error);
      return;
    }
    setSaying(null);
    setWhy('');
    setSheet({ ...got, document: got.document ?? doc });
  };

  return (
    <Paper
      masthead={sheet.state === 'signed' ? 'SIGNED OFF' : sheet.state === 'rejected' ? 'SENT BACK' : 'APPROVAL REQUEST'}
      organisation={doc.organisation}
    >
      <Heading doc={doc} you={you} />

      {/*
        * THE SUBJECT, AND THE GATE ON IT.
        *
        * The two answers stay shut until this has been pressed. It costs one
        * press and buys the sentence an audit needs — this person reviewed this,
        * at this time, before they approved it. A DOCUMENT opens as a file; a
        * SPRINT or a RELEASE shows what is IN it, read-only, from the same press.
        * The gate is said ON the card rather than beside the buttons (Vinnie, 22
        * Aug), and it changes rather than vanishing, so the buttons never jump up
        * under a reaching hand.
        */}
      {subjectKind === 'document' ? (
        <div className="sg-file">
          <div className="sg-file-main">
            <div className="sg-file-what">
              <strong>{doc.fileName || 'The document'}</strong>
              <span>
                {(doc.fileType ?? '').includes('pdf') ? 'PDF' : doc.fileType || 'file'} ·{' '}
                {size(doc.fileSize ?? 0)}
              </span>
            </div>
            <button className="btn" onClick={open} disabled={busy}>
              {you.opened ? 'Show it again' : 'Open the document'}
            </button>
          </div>
          {!answered && !sealed && (
            <p className={`sg-gate${you.opened ? ' is-open' : ''}`}>
              <Icon name={you.opened ? 'check' : 'eye'} size={13} />
              {you.opened ? 'Opened — you can answer below' : 'Open the document to answer'}
            </p>
          )}
        </div>
      ) : (
        <>
          <div className="sg-file">
            <div className="sg-file-main">
              <div className="sg-file-what">
                <strong>{subjectKind === 'sprint' ? 'What this sprint delivered' : 'What’s shipping'}</strong>
                <span>
                  {doc.items?.length ?? 0} item{(doc.items?.length ?? 0) === 1 ? '' : 's'}
                  {subjectKind === 'sprint' && doc.startsOn && doc.endsOn
                    ? ` · ${onThe(doc.startsOn)} – ${onThe(doc.endsOn)}`
                    : ''}
                </span>
              </div>
              <button className="btn" onClick={open} disabled={busy || you.opened}>
                {you.opened
                  ? 'Shown below'
                  : subjectKind === 'sprint'
                    ? 'Show what’s included'
                    : 'Show what’s shipping'}
              </button>
            </div>
            {!answered && !sealed && (
              <p className={`sg-gate${you.opened ? ' is-open' : ''}`}>
                <Icon name={you.opened ? 'check' : 'eye'} size={13} />
                {you.opened ? 'Reviewed — you can answer below' : 'Review what’s included to answer'}
              </p>
            )}
          </div>
          {you.opened && (
            <ul className="sg-review">
              {(doc.items ?? []).map((it, i) => (
                <li key={`${it.ref}-${i}`} className="sg-del">
                  {it.ref && <span className="sg-del-ref">{it.ref}</span>}
                  <span className="sg-del-title">{it.title || 'Untitled'}</span>
                  {it.type && <span className="sg-del-type">{it.type}</span>}
                  {isDone(it.status) && (
                    <span className="sg-del-done">
                      <Icon name="check" size={12} /> Done
                    </span>
                  )}
                </li>
              ))}
              {(doc.items ?? []).length === 0 && (
                <li className="sg-del-empty">Nothing is listed on this yet.</li>
              )}
            </ul>
          )}
        </>
      )}
      {file && (
        <>
          <object className="sg-viewer" data={file} type={doc.fileType || 'application/pdf'}>
            {/* A Word file, or a browser that will not draw a PDF in place.
                Never a dead end: the same address, as something to press. */}
            <p className="sg-note">
              This is not a document your browser can show here.{' '}
              <a href={file} target="_blank" rel="noreferrer">
                Open it in a new tab
              </a>
              .
            </p>
          </object>
          <p className="sg-note">
            <a href={file} target="_blank" rel="noreferrer">
              Open it in a new tab
            </a>{' '}
            to read, print or save it.
          </p>
        </>
      )}

      <table className="sg-who">
        <thead>
          <tr>
            <th>Title</th>
            <th>Name</th>
            <th>Answer</th>
          </tr>
        </thead>
        <tbody>
          {panel.map((row, i) => (
            <tr key={`${row.name}-${i}`} className={row.isYou ? 'is-you' : undefined}>
              <td className="sg-role">{row.position}</td>
              <td className="sg-name">
                {row.name}
                {row.isYou && <span className="sg-tag">you</span>}
              </td>
              <td className="sg-answer">
                {row.answer === 'waiting' ? (
                  <span className="sg-pill is-waiting">Waiting</span>
                ) : row.answer === 'rejected' ? (
                  <>
                    <span className="sg-pill is-rejected">Rejected</span>{' '}
                    <span className="sg-date">{onThe(row.at)}</span>
                    {row.why && <em className="sg-why">{row.why}</em>}
                  </>
                ) : (
                  <>
                    <span className="sg-pill is-approved">Approved</span>{' '}
                    <span className="sg-date">{onThe(row.at)}</span>
                  </>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {sheet.state === 'rejected' && !answered && (
        <p className="sg-stamp is-bad">
          <strong>Somebody has sent this back.</strong> There is nothing to approve until it has
          been revised and sent again.
        </p>
      )}

      {answered ? (
        <>
          <p className={`sg-stamp ${you.answer === 'rejected' ? 'is-bad' : 'is-ok'}`}>
            <strong>
              You {you.answer === 'rejected' ? 'rejected' : 'approved'} this on {when(you.at)}.
            </strong>{' '}
            {you.answer === 'rejected'
              /* NOT "and whoever asked has been told" — nothing tells them yet.
                 That is the email half, and it waits on a mail key. */
              ? 'It goes back to be revised.'
              : 'Nothing further is needed from you.'}
          </p>
          {you.answer === 'rejected' && you.why && <p className="sg-note">You said: {you.why}</p>}
          {/* CHANGING YOUR MIND IS ALLOWED WHILE THE REQUEST IS OPEN — Vinnie,
              22 August — and stops the moment it has been relied on. Both
              answers stay in the trail either way; nothing is erased. */}
          {sealed ? (
            <p className="sg-note">
              {sheet.state === 'signed'
                ? 'Everybody has now answered and the document has been signed off, so answers can no longer be changed.'
                : 'This has been sent back, so answers can no longer be changed.'}
            </p>
          ) : saying ? (
            <Asking
              answer={saying}
              why={why}
              setWhy={setWhy}
              busy={busy}
              onCancel={() => setSaying(null)}
              onConfirm={() => record(saying)}
            />
          ) : (
            <div className="sg-act">
              <button className="link" onClick={() => setSaying(you.answer === 'approved' ? 'rejected' : 'approved')}>
                Change it to {you.answer === 'approved' ? 'rejected' : 'approved'}
              </button>
            </div>
          )}
        </>
      ) : sealed ? null : saying ? (
        <Asking
          answer={saying}
          why={why}
          setWhy={setWhy}
          busy={busy}
          onCancel={() => setSaying(null)}
          onConfirm={() => record(saying)}
        />
      ) : (
        <>
          <div className="sg-act">
            {/* NO DEFAULT IS CHOSEN. An "Approve" already selected is how a
                refusal gets recorded as agreement by somebody moving fast. */}
            <button className="sg-ans is-approved" disabled={!you.opened || busy} onClick={() => setSaying('approved')}>
              Approve
            </button>
            <button className="sg-ans is-rejected" disabled={!you.opened || busy} onClick={() => setSaying('rejected')}>
              Reject
            </button>
          </div>
        </>
      )}

      {problem && (
        <p className="sg-problem" role="alert">
          {problem}
        </p>
      )}
    </Paper>
  );
}

/** The one confirmation, and the one question a rejection owes the sheet. */
function Asking({
  answer,
  why,
  setWhy,
  busy,
  onCancel,
  onConfirm,
}: {
  answer: 'approved' | 'rejected';
  why: string;
  setWhy: (s: string) => void;
  busy: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  const no = answer === 'rejected';
  return (
    <div className="sg-asking">
      <p className="sg-asking-head">
        {no ? (
          <>
            You are <strong>rejecting</strong> this. Say why, so it can be put right.
          </>
        ) : (
          <>
            You are <strong>approving</strong> this. Your name and title go on the sheet against
            today&rsquo;s date.
          </>
        )}
      </p>
      {no && (
        <input
          className="input"
          value={why}
          autoFocus
          placeholder="e.g. the cancellation letter is not in scope"
          onChange={(e) => setWhy(e.target.value)}
        />
      )}
      <div className="sg-act">
        <button className="btn" disabled={busy || (no && why.trim() === '')} onClick={onConfirm}>
          {busy ? 'Recording…' : no ? 'Record the rejection' : 'Record the approval'}
        </button>
        <button className="link" onClick={onCancel}>
          Cancel
        </button>
      </div>
    </div>
  );
}

/** Who is being asked, and what for. */
function Heading({
  doc,
  you,
}: {
  doc: NonNullable<Sheet['document']>;
  you: NonNullable<Sheet['you']>;
}) {
  const KIND: Record<string, string> = {
    requirements: 'Requirements',
    change: 'Change request',
    test: 'Test sheet',
  };
  const subjectKind = doc.subjectKind ?? 'document';
  /* The line under the title names WHAT this is: a document by its kind, a sprint
     or a release by its own word. */
  const sub =
    subjectKind === 'sprint'
      ? 'Sprint sign-off'
      : subjectKind === 'release'
        ? 'Release sign-off'
        : KIND[doc.kind ?? ''] ?? doc.kind ?? 'Document';
  return (
    <>
      <p className="sg-title">{doc.title}</p>
      <p className="sg-sub">
        {sub}
        {doc.board ? ` · ${doc.board}` : ''}
        {subjectKind === 'document' && (doc.version ?? 1) > 1 ? ` · version ${doc.version}` : ''}
      </p>
      <div className="sg-to">
        <b>{you.name}</b>
        <span>
          {you.position}
          {doc.sentAt ? ` · sent for approval on ${onThe(doc.sentAt)}` : ''}
        </span>
      </div>
    </>
  );
}

/**
 * THE SHEET ITSELF, and the reason it does not look like the app.
 *
 * Somebody arriving here has never seen FlightDeck and is not being asked to
 * learn it. The chrome an approver needs is the chrome of a document: a
 * masthead saying whose it is, one column of paper, and nothing to navigate.
 */
function Paper({
  masthead,
  organisation,
  children,
}: {
  masthead: string;
  organisation?: string;
  children: React.ReactNode;
}) {
  return (
    <div className="sg">
      <div className="sg-paper">
        <div className="sg-top">
          <span>FLIGHTDECK · {masthead}</span>
          {organisation && <span>{organisation}</span>}
        </div>
        <div className="sg-body">{children}</div>
      </div>
      <p className="sg-foot">Recorded by FlightDeck. Your answer is kept with the record it was given against.</p>
    </div>
  );
}
